import { Vector2 } from '@babylonjs/core/Maths/math';
import { EasingFunction, CubicEase } from '@babylonjs/core/Animations/easing';

import { MeshSystem } from '../System/meshSystem';
import { GravityGrid } from '../System/gravityGrid';
import { Animation } from '../System/animation';
import { Planet } from '../Objects/planet';
import { StarCategory, StarInterface } from '../Objects/star';
import { BlackHole } from '../Objects/blackHole';
import { MovingEntity } from '../Entity/movingEntity';
import { PlanetMap } from '../Map/planetMap';

export interface PlayerInterface {
    key: string;
    size: number;
    position: any;
    destination: any;
    maxPlanet: number; 
    gravityField: number;
    velocity: number;
    absorbing: string;
    absorbed: string;
    realVelocity: number;
    accelerating: boolean;
}

export class Player extends BlackHole {

    key: string;
    destination: any;
    absorbing: string;
    absorbed: string;
    realVelocity: number = 1;
    target: Player;

    accelerateAnimation: Animation;
    absorbAnimation: Animation;
    particleCurve: EasingFunction;
    ia = false;
    realPlayer = false;
    dustField = true;

    constructor(system: MeshSystem, gravityGrid: GravityGrid, planetMap: PlanetMap, playerInterface: StarInterface) {
        super(system, gravityGrid, planetMap, playerInterface);

        this.key = 'player' +Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        this.accelerateAnimation = new Animation(this.system.animationManager);
        this.absorbAnimation = new Animation(this.system.animationManager);

        this.particleCurve = new CubicEase();
    }

    category: StarCategory;
    setCategory(category: StarCategory, withPlanets: boolean) {
        if (this.category == category) return;
        this.category = category;
        this.updateCategory(category, withPlanets);
    }

    updateCategory(category: StarCategory, withPlanets: boolean) {
        this.setVelocity(category.velocity);
        this.setTemperature(category.temperature);
        this.setMaxPlanet(category.planets)
        this.setGravity(category.gravity);
        this.updateGravityGrid();
        this.removeAllPlanets();
        if (withPlanets) {
            for (let i = 0; i < category.planets; i++) {
                this.addPlanet();
            }
        }
        this.system.checkActiveMeshes();
    }

    setVelocity(velocity: number) {
        this.velocity = velocity;
    }

    updateGravityGrid() {
        if (this.isBlackHole) this.gravityGrid.setBlackHolePoint(this.key, this.position, this.blackHoleSize, this.depth);
        else this.gravityGrid.setStarPoint(this.key, this.position, this.gravityField);
    }
    
    setRealVelocity(realVelocity: number) {
        this.realVelocity = realVelocity;
    }

    distanceAbsorbRatio = 0.002;
    starAbsorbRatio = 2;
    distanceWithTarget: number;
    absorbTarget(target: Player, distanceWithTarget: number) {
        this.distanceWithTarget = distanceWithTarget;
        this.cycleAbsorb = 1 + 50 / distanceWithTarget;
        // Check target to make sure we always absorb closest target
        if (this.absorbing && target == this.target) return;
        if (this.realPlayer || target.realPlayer) {
            this.system.soundManager.fadeIn('absorb');
        }

        this.absorbStop();
        this.absorbing = target.key;
        this.target = target;
        this.setAbsobUpdateFunction();
        this.system.checkActiveMeshes();
        let lastCount = 0;
        this.absorbAnimation.infinite((count) => {
            let change = count - lastCount;
            lastCount = count;
            let sizeSpeed = this.target.size * this.starAbsorbRatio;
            let distanceSpeed = this.distanceAbsorbRatio / this.distanceWithTarget;
            let up = change * distanceSpeed / sizeSpeed;
            this.changeSize(up);
            let down = -change * distanceSpeed * sizeSpeed;
            this.target.changeSize(down);
            if (this.target.isDead) this.absorbStop();
        });
    }

    blackHoleAbsorbRatio = 10;
    blackHoleAbsorber: BlackHole;
    absorbByBlackHole(absorber: BlackHole, distanceWithTarget: number) {
        this.distanceWithTarget = distanceWithTarget;

        if (this.blackHoleAbsorber) return;
        this.absorbStop();
        this.blackHoleAbsorber = absorber;
        this.absorbing = absorber.key;
        this.setGetAbsobByBlackHoleFunction();

        let lastCount = 0;
        this.absorbAnimation.infinite((count) => {
            let change = count - lastCount;
            lastCount = count;
            let sizeSpeed = this.absorber.size * this.blackHoleAbsorbRatio;
            let distanceSpeed = this.distanceAbsorbRatio / this.distanceWithTarget;
            let up = change * distanceSpeed / sizeSpeed;
            this.absorber.changeSize(up);
            let down = -change * distanceSpeed * sizeSpeed;
            this.changeSize(down);
            // if (this.absorber.isDead) this.absorbStop();
        });
    }

    setAbsorber(absorber: MovingEntity, proximity: number) {
        this.absorber = absorber;
        let newVelocity = Math.pow(proximity, 1/2)
        this.setRealVelocity(newVelocity);
    }

    absorbStop() {
        if (!this.absorbing) return;
        this.absorbStopParticle();
        if (this.realPlayer || this.target.realPlayer) {
            this.system.soundManager.fadeOut('absorb');
        }
        this.absorbAnimation.stop();
        this.absorbing = null;
        this.blackHoleAbsorber = null;
        this.setRealVelocity(1);
    }

    direction: Vector2 = Vector2.Zero();
    starVelocity: 0.03;
    move(step: Vector2) {
        if (step.y == 0) step.y = 0.001;
        step = step.normalize();
        let finalVelocity = this.velocity * this.realVelocity / (Math.sqrt(this.size) * 5);
        this.currentMove = Vector2.Lerp(this.currentMove, step, 0.05);
        this.direction = new Vector2(this.currentMove.x * finalVelocity, this.currentMove.y * finalVelocity);
        let pos = this.position.add(this.direction);
        this.setPosition(pos);
    }

    setPosition(pos: Vector2) {
        this._setPosition(pos);
        this.transformMesh.position.x = this.position.x;
        this.transformMesh.position.z = this.position.y;
        this.updateGravityGrid();
    }

    addPlanet(planet?: Planet) {
        let planetNumber = this.satellites.length;
            const radius = this.getSatelliteRadius(planetNumber);
            let velocity = 1 / (1 + planetNumber / 2) + Math.random() / 2;
        if (!planet) {
            planet = new Planet(this.system);
            planet.setGeostationnaryMovement(radius, velocity);
            planet.show();
        } else {
            this.grabSatellite(planet, radius, velocity);
            if (this.realPlayer) this.system.soundManager.play('catchPlanet');
        }
        this.fixeSatellite(planet);
    }

    addDust() {
        // if (this.realPlayer) this.system.soundManager.play('catchDust');
        this.changeSize(0.005 / (Math.pow(this.size, 3)));
    }
    
    launchAnimationLength = 80;
    accelerate(callback?: Function): boolean {
        const satNumber = this.satellites.length;
        if (!this.moving || this.accelerating || !this.isStarVisible || !satNumber) return false;
        let planet = this.satellites[satNumber - 1];

        // this.system.soundManager.playMesh('accelerate', this.transformMesh);
        if (this.realPlayer) this.system.soundManager.play('accelerate');

        this.accelerating = true;
        this.accelerateAnimation.simple(this.launchAnimationLength, (count, perc) => {
            const { stationaryRadius, stationaryVelocity } = planet;
            const newstationaryRadius = Math.max(stationaryRadius - 0.1, 0);
            const newStationaryVelocity = stationaryVelocity + 0.05;
            planet.setGeostationnaryMovement(newstationaryRadius, newStationaryVelocity);
            // if (perc < 0.5) this.realVelocity = 1 + 4 * perc;
            // else this.realVelocity = 1 + 4 * Math.max(1 - perc, 0);
            this.realVelocity = 1 + 2 * Math.sin(perc * Math.PI);
        }, () => {
            this.accelerating = false;
            this.planetMap.storagePlanet(planet);
            this.realVelocity = 1;
            this.satellites.pop()
            if (callback) callback();
        });

        return true
    }
    
    onDied: Function;
    die(callback?: Function) {
        if (this.isDead) return;
        this.removeAllPlanets();
        if (this.absorber && this.absorber.isBlackHole) {
            setTimeout(() => {
                this.dispose();
                if (callback) callback();
             }, 5000);
            this.setHeartScale(0);
            this.surfaceParticles.stop();
            this.coronaParticles.stop();
            this.trailParticles.stop();
        } else {
            this.explode(() => {
                setTimeout(() => {
                    this.dispose();
                }, 2000);
                if (callback) callback();
            });
        }
        this.absorbStop();
        this.setMoving(false);
        this.isDead = true;
        if (this.onDied) this.onDied();
    }
    
    dispose() {
        this._disposePlayer();
    }
    
    _disposePlayer() {
        this.isDead = true;
        this.moveCatcher.stop();
        this.accelerateAnimation.stop();
        this.removeAllPlanets();
        this._disposeStarFighter();
        this.absorbStop();
        this.gravityGrid.eraseMass(this.key);
    }

    // For IA
    goToPlayer(player: Player) {
        this.moveCatcher.catch(player.position.subtract(this.position));
    }
}